import {useState, useEffect, useMemo, useCallback,
  ReactElement, useContext} from 'react'
import {Warehouse} from "./api/warehouse"
import { useParams, useNavigate } from "react-router-dom";
import {ScenariosTable} from "./ScenariosTable"
import "./Scenarios.scss"
import {useDivision} from "./shared"
import {AllDataContext} from "./LoggedinMainPage"
import TextAreaWithSuggestions from "./inputs/TextAreaWithSuggestions"

export default function Scenarios(): ReactElement {
  const allData = useContext(AllDataContext)
  const {warehouses, items} = allData
  const division = useDivision()
  const [scenarioLinesString, setScenarioLinesString] = useState("")
  const warehouseCodeParam = useParams().warehouseCode ?? ""
  const warehouseCodes = useMemo(
    () => warehouseCodeParam.split(",").map(s => s.trim()).filter(s => s.length),
    [warehouseCodeParam])
  const selectedWarehouses = useMemo(() => warehouseCodes.map(
    // TODO: make sure that a warehouse is not selected twice
          warehouseCode => warehouses.find(w => w.code === warehouseCode)),
          [warehouses, warehouseCodes])
  const selectedWarehousesLoadedAndReady = selectedWarehouses.length > 0 &&
    selectedWarehouses.every(wh => wh)
  const scenarioLinesStringParam = useParams().scenarioLines ?? ""
  const itemPickerTrigger = useMemo(() => {
    const itemPickerData = allData.items.map(item => [
      item.code, item.code + " \u2014 " + item.description] as [string, string])
    return {"#": itemPickerData}
  }, [allData.items])
  const navigate = useNavigate()

  useEffect(() => {
    setScenarioLinesString(scenarioLinesStringParam)
  }, [scenarioLinesStringParam])

  const scenarioLines = scenarioLinesString.trim().split("\n")

  const doNavigate = useCallback(
    ({newWarehouseCodes, newScenarioString, replace}:
      {newWarehouseCodes?: readonly string[], newScenarioString?: string,
        replace?: boolean,}) => {
      navigate([
        "",
        "division",
        division,
        "Scenarios",
        "warehouse",
        (newWarehouseCodes === undefined ? warehouseCodes :newWarehouseCodes)
          .join(",") || ",",
        "scenariolines",
        newScenarioString === undefined ? scenarioLinesString : newScenarioString,
      ].map(encodeURIComponent).join("/"), {replace: replace ?? false})
    }, [navigate, division, warehouseCodes, scenarioLinesString])


  useEffect(function selectMainWarehouseIfNoneSelected() {
    if (warehouses.find(w => w.code === warehouseCodes[0])) {
      return
    }
    const mainWarehouse = warehouses.find(w => w.main === 1) ?? warehouses[0]
    if (mainWarehouse) {
      doNavigate({newWarehouseCodes: [mainWarehouse.code], replace: true})
    }
  }, [doNavigate, division, warehouseCodes, warehouses])

  return <div className="Scenarios">
    <h2>Hypothetical Scenario Builder</h2>
    <div className="warehouseSelector">
      {[...warehouseCodes, ""].map(
        (warehouseCode, index) =>
         index === warehouses.length ? ""  // no more warehouses to select
         : <div key={index}>
          {index === 0 ? <span>Select a warehouse: </span>
          : <span>Optionally select an additional warehouse: </span>}
          <select data-cy={`select-warehouse-${index}`}
            value={warehouseCode} onChange={e => {
              let newWarehouseCodes = [...warehouseCodes]
              if (e.target.value === "") {
                newWarehouseCodes.splice(index, 1)
              } else {
                newWarehouseCodes.splice(index, 1, e.target.value)
              }
              newWarehouseCodes = Array.from(new Set(newWarehouseCodes))
              doNavigate({newWarehouseCodes})
            }}>
            {index > 0 && <option value="">---</option>}
            {[...warehouses]
              .filter(wh => !warehouseCodes.slice(0, index).find(
                c => c === wh.code))
              .sort((a, b) => a.code.localeCompare(b.code)).map(
              warehouse => <option key={warehouse.code} value={warehouse.code}>
                {warehouse.code} - {warehouse.description}
              </option>
            )}
          </select>
        </div>
      )}
    </div>
      <h3>Creating a hypothetical scenario, based on the following
        warehouses: {warehouseCodes.join(", ")}</h3>
      <details>
        <summary>
          (click for short manual and explanation)
        </summary>
        <p>
          This page allows building a hypothetical sales order scenario.
          It allows one to answer the question "if I were to receive a certain
          sales order today, how fast could I ship it".
        </p>
        <p>
          The first step is to select a warehouse above that you would like
          to ship the order from.
          You can select more than one warehouse; the scenario builder will first
          try to ship from the first warehouse, then ship from the first and
          second warehouse combined, then the first three combined, etc.
        </p>
        <p>
          Secondly, type in the text field below what the order consists of.
          Each line should start with a number (how many items are ordered),
          followed by ann item code.
          Use a new line per item; note that each item can only occur once in
          the list.
        </p>
        <p>
          After typing the order in the text field, below your scenario will be
          built.
          The first three columns show the number of items ordered, and the
          item code and description.
          The columns afterwards show on which dates how many items (and which
          percentage of the order) can be shipped.
        </p>
        <p>
          Each column (after the date column) shows, per date, the number
          of available items in this and all previous warehouses.
          So the number in the third warehouse column shows the number of
          available items in the first, second and third warehouse combined.
          It also shows the percentage of the order that can be fulfilled with
          these items.
        </p>
        <p>
          If the letters are red in one of the columns, it means that there is
          actually a <em>negative</em> projected available stock at this time.
          Normally this means that this should be investigated and fixed before
          continuing, since the results may be unreliable else.
        </p>
        <p>
          The counts show a progress bar going from light-red to light-green to
          show which part of the order can be fulfilled by this amount of items.
          A field with a dark-green background indicates that here the whole order
          can be fulfilled.
        </p>
        <p>
          The last row shows a summary for the whole order.
          This allows you to quickly see when the whole order can be shipped,
          (and when partial shipments can be made).
        </p>
        <p>
          At this moment there is no way to generate a sales order from the
          scenario builder; this is something that will have to be done
          manually for now.
        </p>
      </details>
      <div className="scenarioLines">
        <p>
          Your scenario:
          On each line, write a number (number of items) and an item code.
        </p>
        <TextAreaWithSuggestions
          triggers={itemPickerTrigger}
          setValue={setScenarioLinesString}
          onBlur={e => doNavigate({newScenarioString: e.target.value})}
          style={{height:
            `${scenarioLinesString.split("\n").length * 1.3 + 2.5}em`}}
          id="scenario"
          placeholder="6 MYP13-32Y" value={scenarioLinesString} />
      </div>
    {selectedWarehousesLoadedAndReady &&
    <ScenariosTable lines={scenarioLines}items={items}
      warehouses={selectedWarehouses as Warehouse[]} />
    }
  </div>
}
